// --- START OF FILE ToDoPage.js ---

import React, { useCallback, useMemo, useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css"; // Default calendar styles
import { FaTrash, FaCalendarAlt } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import styles from "./ToDoPage.module.css"; // Import CSS Modules
import { useData } from "./../context/DataContext"; // Context hook
import { MENU_STATUS } from "./../config/MenuInfluencerStatus"; // Import Status Menu options
import Spinner from "./Spinner"; // Loading spinner component
import { formatDateToYYYYMMDD } from "../utils/HelperFunctions";
import ConfirmationModal from '../utils/ConfirmationModal';


/**
 * Calculates distributed dates for tasks across a range, skipping weekends.
 * @param {Array<object>} influencers - Array of influencer objects { influencerId, name, ... }
 * @param {Date} startDate - The starting date (usually today).
 * @param {Date} endDate - The target due date from the calendar.
 * @returns {Array<{influencerId: number, date: string}>} Array mapping influencerId to a calculated 'YYYY-MM-DD' date.
 */
function calculateDistributedDates(influencers, startDate, endDate) {
    if (!influencers || influencers.length === 0)
      return [];
    
    if (!(startDate instanceof Date) || !(endDate instanceof Date) || startDate > endDate) {
      console.error("Invalid dates for distribution", startDate, endDate);
      
      // Fallback: assign all to end date
      const endDateStr = formatDateToYYYYMMDD(endDate);

      // FIX: Add defensive checks for the influencers array.
      return (influencers || [])
          .filter(inf => typeof inf === 'object' && inf !== null)
          .map(inf => ({
              influencerId: inf?.influencerId, // Use optional chaining for safety
              date: endDateStr
          }));
    }

    const workingDays = [];
    let currentDate = new Date(startDate);

    // Ensure start date is not a weekend if it's the only day
    if (startDate.getTime() === endDate.getTime() && (currentDate.getDay() === 0 || currentDate.getDay() === 6)) {
         console.warn("Distribution date is a weekend, assigning tasks anyway.");
         workingDays.push(new Date(currentDate));
    } else {
        // Collect all working days between start (inclusive) and end (inclusive)
        while (currentDate <= endDate) {
            const dayOfWeek = currentDate.getDay();
            if (dayOfWeek !== 0 && dayOfWeek !== 6) { // 0 = Sunday, 6 = Saturday
                workingDays.push(new Date(currentDate));
            }
            currentDate.setDate(currentDate.getDate() + 1);
        }
    }


    if (workingDays.length === 0) {
      // If no working days found (e.g., start/end are same weekend day and not the only day), assign all to end date
      console.warn("No working days found for distribution range, assigning all to end date.");
      const endDateStr = formatDateToYYYYMMDD(endDate);

      // FIX: Add defensive checks for the influencers array.
      return (influencers || [])
          .filter(inf => typeof inf === 'object' && inf !== null)
          .map(inf => ({
              influencerId: inf?.influencerId, // Use optional chaining for safety
              date: endDateStr
          }));
    }

    const assignments = [];
    const numInfluencers = influencers.length;
    const numDays = workingDays.length;

    for (let i = 0; i < numInfluencers; i++) {
      const influencer = influencers[i];
      // Cycle through working days using modulo operator
      const dayIndex = i % numDays;
      const assignedDate = workingDays[dayIndex];
      assignments.push({
          influencerId: influencer.influencerId,
          date: formatDateToYYYYMMDD(assignedDate),
      });
    }

    console.log("Distributed Assignments:", assignments);
    return assignments;
}


// --- Component ---
const ToDoPage = () => {
  // Destructure values and functions from DataContext
  const {
    queryResults, // Array of influencer objects defining the "group"
    setQueryResults,
    activeinfluencerId, // ID of the currently focused influencer (null if none/group/all)
    setActiveinfluencerId, // Function to set the active influencer ID
    fetchInfluencerData,
    fetchBatchInfluencerData,
    tasks, // Global array of task objects (date is 'YYYY-MM-DD' string)
    taskTemplates, // <<< NEW: Get templates from context
    createTask, // API wrapper to create a task
    updateTask, // API wrapper to update a task
    deleteTask, // API wrapper to delete a task
    rescheduleTasks, // <-- NEW API wrapper
    addTask, // Dispatch function to add task to global state
    toggleTaskCompletion, // Dispatch function to toggle completion in global state
    updateTasksDate, // <-- NEW dispatch function
    deleteTaskById, // Dispatch function to delete task from global state
    selectedDate, // Currently selected JS Date object for the calendar
    setSelectedDate, // Function to update the selectedDate
    isQueryMode, // Boolean indicating if we are viewing a specific query group context
    setQueryMode, // Function to set query mode on/off
    loading, // Boolean loading state for tasks fetch

  } = useData();

  const navigate = useNavigate();

  // --- Local State ---
  const [newTask, setNewTask] = useState(""); // Input field for new manual/group task
  const [localError, setLocalError] = useState(null); // Error messages specific to this page
  const [isSaving, setIsSaving] = useState(false); // Loading state for CUD operations
  const [isGroupView, setIsGroupView] = useState(false); // State to manage view *within* query mode
  const [distributeTasks, setDistributeTasks] = useState(false); // State for group task distribution
  const [statusFilter, setStatusFilter] = useState("ALL"); // For influencer status filter
  const [completionFilter, setCompletionFilter] = useState("not completed"); // For task completion status filter
  const [influencerNames, setInfluencerNames] = useState({});
  const [rescheduleMode, setRescheduleMode] = useState(false); // <-- NEW: Toggle reschedule UI
  const [selectedForReschedule, setSelectedForReschedule] = useState(new Set()); // <-- NEW: Task IDs to reschedule
  const [rescheduleDate, setRescheduleDate] = useState(formatDateToYYYYMMDD(new Date())); // <-- NEW: Target date for rescheduling
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false); // <-- NEW: Modal visibility state
  const [modalMessage, setModalMessage] = useState(""); // <-- NEW: Modal dynamic message state


  // --- Derived State / Memos ---

  // Deriving active influencer details (needed for creating tasks / header display)
  const activeInfluencer = useMemo(() => {
    if (!Array.isArray(queryResults)) return null;
    return (queryResults || []).find((item) => item.influencerId === activeinfluencerId);
  }, [queryResults, activeinfluencerId]);

  const activeInfluencerStatus = activeInfluencer?.status || "";
  

  // --- UPDATED: Derive valid Go! task types from fetched templates ---
  const validGoTypes = useMemo(() => {
      // FIX: Filter out invalid items before mapping to prevent crashes and undefined values.
      const templateTypes = (taskTemplates || [])
          .filter(task => task && typeof task === 'object' && task.taskType !== undefined)
          .map(task => task.taskType);

      return [...templateTypes, "AUTOEMAIL", "AUTOGROUP"];
  }, [taskTemplates]);

  const queryModeResults = useMemo(() => {
    return queryResults || []; // Ensure it's an array
  }, [queryResults]);

  // --- Effects ---

  // Effect to manage group view state based on query mode
  useEffect(() => {
    if (!isQueryMode) {
      setIsGroupView(false);
      // Active influencer ID reset is handled by handleShowAllTasks
    }
  }, [isQueryMode]);
  

  // Step 3: Use useEffect to pre-fetch names before rendering
  useEffect(() => {
    const preloadNames = async () => {
      // 1. Get unique IDs of influencers from the tasks that don't already have names loaded.
      const existingIds = new Set(Object.keys(influencerNames));
      const neededIds = [
          ...new Set(
              // 1. FIX: Provide a fallback if 'tasks' is not an array.
              (tasks || [])
                  // 2. FIX: Filter out any null or non-object items from the 'tasks' array.
                  .filter(task => typeof task === 'object' && task !== null)
                  // This filter is now safe to run.
                  .filter(task => task.influencerId && task.influencerId !== 0)
                  .map(task => task.influencerId)
                  // 3. FIX: Provide a fallback if 'existingIds' is not a Set.
                  .filter(id => !(existingIds || new Set()).has(id.toString()))
          ),
      ];
      
      if (neededIds.length === 0) {
        return; // Nothing to fetch
      }

      console.log("Fetching batch data for needed influencer IDs: ", neededIds);

      // 2. Make a SINGLE API call to the new batch endpoint
      const result = await fetchBatchInfluencerData(neededIds);

      if (result.status && result.data) {
        // 3. Process the results and update the state
        const namesObj = result.data.reduce((acc, influencer) => {
          // Assuming the backend returns { influencerId, name } for each
          acc[influencer.influencerId] = influencer.name;
          return acc;
        }, {});

        // Merge new names with existing names to avoid re-renders
        setInfluencerNames(prev => ({ ...prev, ...namesObj }));
      } else {
        console.error("Failed to fetch batch influencer names:", result.message);
      }
    };

    preloadNames();
    // Depend on `tasks` to trigger the fetch, but fetchBatchInfluencerData is stable.
  }, [tasks, fetchBatchInfluencerData]);

  // --- Helper Functions ---
  
  // Gets influencer name based on ID (checks queryResults first, then mockData)
  // Gets influencer name based on ID (checks queryResults first, then preloaded names)
  const getChannelName = useCallback(
    (influencerId) => {
      if (!influencerId || influencerId === 0) 
        return "";
      
      // Primary source of truth is the preloaded names
      if (influencerNames[influencerId]) {
          return influencerNames[influencerId];
      }
      
      // Fallback for cases where it's already in the main query results
      if (Array.isArray(queryResults)) {
          const foundInQuery = queryResults.find((c) => c.influencerId === influencerId);
          if (foundInQuery) return foundInQuery.name;
      }
      
      // If not found anywhere, it's either loading or failed to load
      return "Loading..."; // <-- CHANGE THIS from "Unknown"
    },
    [queryResults, influencerNames]
  );
    
  // --- NEW: Handler for influencer selection by clicking task container ---
  // --- NEW: Refactored Handler for influencer selection ---
  const handleInfluencerSelect = useCallback(async (influencerId) => {
    if (rescheduleMode || !influencerId || influencerId === 0) return;

    // Don't navigate away if we are already viewing this influencer.
    if (!isGroupView && activeinfluencerId === influencerId) return;

    console.log(`Transitioning to influencer: ${influencerId}`);
    setIsSaving(true); // Use a loading state to prevent other actions
    setLocalError(null);

    try {
        // STEP 1: Fetch the data for the influencer BEFORE changing the view.
        const influencerDetails = await fetchInfluencerData(influencerId);
        
        if (influencerDetails && !influencerDetails.error) {
            // STEP 2: Once data is successfully fetched, update ALL related state at once.
            setQueryResults(influencerDetails); // Set the context with the single influencer's data
            setActiveinfluencerId(influencerId);
            setIsGroupView(false); // Ensure we are in single-influencer view
            setQueryMode(true);    // Ensure we are in query mode
        } else {
            // Handle the case where fetching the influencer failed
            throw new Error(influencerDetails?.message || "Could not load influencer details.");
        }
    } catch (error) {
        console.error("Error during influencer selection transition:", error);
        setLocalError(error.message);
    } finally {
        setIsSaving(false);
    }
  }, [
    rescheduleMode, 
    isGroupView, 
    activeinfluencerId, 
    fetchInfluencerData, 
    setQueryResults, 
    setActiveinfluencerId, 
    setQueryMode, 
    setIsGroupView
]);

// --- Action Handlers ---

  // NEW: This handler contains the logic to create group tasks and is called on modal confirmation.
  const handleConfirmGroupAddTask = useCallback(async () => {
    setIsConfirmModalOpen(false); // Close the modal

    if (newTask.trim() === "" || isSaving || rescheduleMode) return;
    setLocalError(null);
    setIsSaving(true);
    
    try {
      // --- GROUP TASK CREATION (AUTOGROUP) ---
      const taskText = newTask.trim();
      const influencersInGroup = queryResults || [];

      if (influencersInGroup.length === 0) {
        setLocalError("No influencers in the current group to assign tasks to.");
        return;
      }

      let assignments = [];
      // Use a clean copy of the selected date and zero out the time for accurate comparison
      const dueDate = new Date(selectedDate);
      dueDate.setHours(0, 0, 0, 0);

      if (distributeTasks) {
          // THIS LOGIC IS NOW CORRECTED TO MATCH THE MODAL'S LOGIC
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // Determine the correct start of the distribution range
          const startDateForDist = today > dueDate ? new Date(dueDate) : new Date(today);
          
          // Call the distribution function with the correct, normalized date range
          assignments = calculateDistributedDates(influencersInGroup, startDateForDist, dueDate);
      } else {
          const dueDateStr = formatDateToYYYYMMDD(dueDate);
          assignments = (influencersInGroup || [])
              // FIX: Filter out any null or non-object items to prevent crashes.
              .filter(inf => typeof inf === 'object' && inf !== null)
              .map(inf => ({
                  // FIX: Use optional chaining to safely access the influencerId.
                  influencerId: inf?.influencerId,
                  date: dueDateStr
              }));
      }

      console.log(`Creating AUTOGROUP tasks. Distribution: ${distributeTasks}. Assignments:`, assignments);
      const createdTasks = [];
      let success = true;

      for (const assignment of assignments) {
          const taskPayload = {
            text: taskText,
            completed: false,
            type: "AUTOGROUP",
            nameMenu: "",
            prompt: taskText,
            date: assignment.date,
            influencerId: assignment.influencerId,
          };

          try {
             const apiResult = await createTask(taskPayload);
             if (apiResult && apiResult.todoId) {
                createdTasks.push({ ...taskPayload, id: apiResult.todoId });
             } else {
                 success = false;
                 console.error("Error creating group task for influencer", assignment.influencerId, ":", apiResult?.message);
                 setLocalError(prev => `${prev ? prev + '\n' : ''}Failed to create task for influencer ${getChannelName(assignment.influencerId)}.`);
             }
          } catch (error) {
              success = false;
              console.error("Exception creating group task for influencer", assignment.influencerId, ":", error);
              setLocalError(prev => `${prev ? prev + '\n' : ''}Error creating task for influencer ${getChannelName(assignment.influencerId)}.`);
          }
      } // End for loop

      if (createdTasks.length > 0) {
          createdTasks.forEach(task => addTask(task));
      }

      if (success) {
        setNewTask("");
        setDistributeTasks(false);
      }
    } finally {
        setIsSaving(false);
    }
  }, [
    newTask, selectedDate, isQueryMode, isGroupView, distributeTasks, isSaving, rescheduleMode,
    queryResults, getChannelName, createTask, addTask,
    setNewTask, setDistributeTasks, setLocalError, setIsSaving,
  ]);

  // Refactored Add Task Handler
  const handleAddTask = useCallback(async () => {
    if (newTask.trim() === "" || isSaving || rescheduleMode) return;

    if (isQueryMode && isGroupView) {
      // --- GROUP TASK: Open confirmation modal ---
      setLocalError(null);
      const influencersInGroup = queryResults || [];
      const numTasks = influencersInGroup.length;

      if (numTasks === 0) {
        setLocalError("No influencers in the current group to assign tasks to.");
        return;
      }

      // Use a clean copy of the selected date from the calendar
      const dueDate = new Date(selectedDate);
      dueDate.setHours(0, 0, 0, 0);

      let effectiveStartDate = new Date(dueDate); // Default to due date if not distributing
      let workingDaysCount = 1;

      if (distributeTasks) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // Determine the overall range start date
          const rangeStartDate = today > dueDate ? new Date(dueDate) : new Date(today);
          
          // Find the actual first working day for the message and task assignments
          let firstWorkingDay = null;
          let searchDate = new Date(rangeStartDate);
          while(searchDate <= dueDate) {
              const dayOfWeek = searchDate.getDay();
              if (dayOfWeek !== 0 && dayOfWeek !== 6) { // It's a weekday
                  firstWorkingDay = new Date(searchDate); // Found it!
                  break; // Exit loop
              }
              searchDate.setDate(searchDate.getDate() + 1);
          }
          
          // If no working days are found in the range, default to the due date
          effectiveStartDate = firstWorkingDay || new Date(dueDate);

          // Now, count all working days in the original range for the average calculation
          let count = 0;
          let counterDate = new Date(rangeStartDate);
          while (counterDate <= dueDate) {
              const dayOfWeek = counterDate.getDay();
              if (dayOfWeek !== 0 && dayOfWeek !== 6) {
                  count++;
              }
              counterDate.setDate(counterDate.getDate() + 1);
          }
          workingDaysCount = count > 0 ? count : 1; // Avoid division by zero
      }

      const avgTasks = (numTasks / workingDaysCount).toFixed(1);
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      const startDateStr = effectiveStartDate.toLocaleDateString(undefined, options);
      const endDateStr = dueDate.toLocaleDateString(undefined, options);
      
      const message = (
        <p>
          Are you sure you want to create{' '}
          <span className={styles.highlightText}>{numTasks}</span> tasks starting on{' '}
          <span className={styles.highlightText}>{startDateStr}</span> and ending on{' '}
          <span className={styles.highlightText}>{endDateStr}</span>, resulting on an average of{' '}
          <span className={styles.highlightText}>{avgTasks}</span> tasks per working day ?
        </p>
      );

      setModalMessage(message); // State now holds a JSX element
      setIsConfirmModalOpen(true);
      return; // Stop here and wait for user confirmation

    } else {
      // --- SINGLE/MANUAL TASK CREATION (Original Logic) ---
      setLocalError(null);
      setIsSaving(true);
      try {
        const formattedDate = formatDateToYYYYMMDD(selectedDate);
        const influencerIdForTask = (isQueryMode && activeinfluencerId && !isGroupView)
            ? activeinfluencerId
            : 0;
        const currentStatus = (influencerIdForTask === activeinfluencerId) ? activeInfluencerStatus : '';

        const taskPayload = {
          text: newTask.trim(),
          completed: false,
          status: currentStatus,
          type: "MANUAL",
          nameMenu: "",
          prompt: newTask.trim(),
          date: formattedDate,
          influencerId: influencerIdForTask,
        };

        const apiResult = await createTask(taskPayload);
        if (apiResult && apiResult.todoId) {
            addTask({ ...taskPayload, id: apiResult.todoId });
            setNewTask("");
        } else {
            throw new Error(apiResult?.message || "Task creation failed: No ID returned.");
        }
      } catch (error) {
          console.error("Error adding manual task:", error);
          setLocalError(error.message || "Failed to add task.");
      } finally {
          setIsSaving(false);
      }
    }
  }, [
      newTask, selectedDate, isQueryMode, activeinfluencerId, isGroupView, distributeTasks, isSaving, rescheduleMode,
      activeInfluencerStatus, queryResults, createTask, addTask,
      setNewTask, setLocalError, setIsSaving,
  ]);

  const onDateChange = useCallback((date) => {
      setSelectedDate(date);
      // Optionally reset completion filter when date changes?
      // setCompletionFilter("not completed");
  }, [setSelectedDate]);

  const handleKeyPress = useCallback((e) => {
      if (e.key === "Enter" && !isSaving) {
          handleAddTask();
      }
  }, [handleAddTask, isSaving]); // Added isSaving dependency

  // --- Header Navigation/Mode Handlers ---

  // Exits Query Mode -> Shows ALL tasks for the user
  const handleShowAllTasks = useCallback(() => {
    setQueryMode(false);
    setActiveinfluencerId(null); // Clear any active influencer focus
    setQueryResults([]);         // <-- ADD THIS LINE
    setIsGroupView(false);       // Ensure group view is off
    setStatusFilter("ALL");      // Reset status filter
}, [setQueryMode, setActiveinfluencerId, setQueryResults, setIsGroupView, setStatusFilter]); // <-- ADD setQueryResults HERE

  // Enters/Switches to Group View within Query Mode
  const handleShowGroupTasks = useCallback(() => {
    if (!queryModeResults || queryModeResults.length === 0) return; // Prevent if no group
    if (!isQueryMode) {
        setQueryMode(true); // Enter query mode if not already in it
    }
    setIsGroupView(true); // Set group view state
    setActiveinfluencerId(null); // Clear specific influencer focus for group view
    setStatusFilter("ALL"); // Reset status filter when switching to group view
    // Optionally reset completion filter?
    // setCompletionFilter("not completed");
  }, [isQueryMode, setQueryMode, queryModeResults, setActiveinfluencerId, setIsGroupView, setStatusFilter]);

  // Handles selection from the influencer dropdown (in Group View)
  const handleSelectInfluencerFromDropdown = useCallback((event) => {
      const selectedId = parseInt(event.target.value, 10);
      if (!isNaN(selectedId) && selectedId !== 0) { // Check for valid ID (not placeholder)
          setActiveinfluencerId(selectedId); // Set the newly selected influencer
          setIsGroupView(false); // Exit group view, enter single influencer view
          if (!isQueryMode) {
              setQueryMode(true); // Ensure we stay/enter query mode
          }
           // Status filter is hidden in this view, no need to reset explicitly
           // Optionally reset completion filter?
           // setCompletionFilter("not completed");
      }
  }, [setActiveinfluencerId, isQueryMode, setQueryMode, setIsGroupView]);
  
  // Navigate to previous influencer in query results (single view only)
  const handlePrevious = useCallback(() => {
    if (!isQueryMode || isGroupView || !activeinfluencerId || !queryResults) return;
    const currentIndex = queryResults.findIndex(item => item.influencerId === activeinfluencerId);
    if (currentIndex > 0) {
      setActiveinfluencerId(queryResults[currentIndex - 1].influencerId);
       // Optionally reset completion filter?
       // setCompletionFilter("not completed");
    }
  }, [queryResults, activeinfluencerId, isQueryMode, isGroupView, setActiveinfluencerId]);

  // Navigate to next influencer in query results (single view only)
  const handleNext = useCallback(() => {
    if (!isQueryMode || isGroupView || !activeinfluencerId || !queryResults) return;
    const currentIndex = queryResults.findIndex(item => item.influencerId === activeinfluencerId);
    if (currentIndex < queryResults.length - 1) {
      setActiveinfluencerId(queryResults[currentIndex + 1].influencerId);
       // Optionally reset completion filter?
       // setCompletionFilter("not completed");
    }
  }, [queryResults, activeinfluencerId, isQueryMode, isGroupView, setActiveinfluencerId]);

  // --- Filtering & Data Derivation Logic ---

  // Memoized Set of influencer IDs from the current query results for efficient lookup
  const queryInfluencerIds = useMemo(() => {
    // 1. Filter out any null, undefined, or non-object items, and items missing an ID.
    const validIds = (queryResults || [])
        .filter(item => item && typeof item === 'object' && item.influencerId !== undefined)
        .map(item => item.influencerId);

    // 2. Create a Set from the valid IDs.
    return new Set(validIds);
  }, [queryResults]);

  // Determines if a calendar date tile should be marked as having tasks (dot indicator)
  // This logic remains unchanged, as it just indicates *if* tasks exist on a date,
  // regardless of the current filters applied to the list view.
  // --- FIX: Calendar dot now only shows for INCOMPLETE tasks ---
  const hasTasksOnDate = useCallback(
      (date) => {
        const dateStrToCompare = formatDateToYYYYMMDD(date);
        const tasksToCheck = Array.isArray(tasks) ? tasks : [];
  
        const checkCondition = (task) => 
          task.date === dateStrToCompare && 
          task.text?.trim() !== "" && 
          !task.completed; // <-- The critical fix
  
        if (!isQueryMode) {
          return tasksToCheck.some(checkCondition);
        } else if (isGroupView) {
          return tasksToCheck.some(t => queryInfluencerIds.has(t.influencerId) && checkCondition(t));
        } else {
          return tasksToCheck.some(t => t.influencerId === activeinfluencerId && checkCondition(t));
        }
      },
      [isQueryMode, isGroupView, activeinfluencerId, tasks, queryInfluencerIds]
    );


  // Refactored filtering logic incorporating all filters
  const filteredTasks = useMemo(() => {
    const tasksToFilter = Array.isArray(tasks) ? tasks : [];
    const selectedDateStr = formatDateToYYYYMMDD(selectedDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Apply filters step-by-step
    let partiallyFiltered = tasksToFilter.filter((task) => {
        // 1. Primary Filter: Completion / Overdue / Date
        switch (completionFilter) {
            case "all not overdue": {
              if (task.date !== selectedDateStr) return false;
                break;
            }
            case "completed":
                if (task.date !== selectedDateStr || !task.completed) return false;
                break;
            case "overdue":
                const taskofDate = new Date(task.date + 'T00:00:00');
                if (isNaN(taskofDate) || taskofDate >= today || task.completed) return false;
                break;
            case "not completed":
            default:
                if (task.date !== selectedDateStr || task.completed) return false;
                break;
        }

        // Exclude tasks with empty text
        if (task.text?.trim() === "") return false;

        // 2. Mode Filter (All / Group / Single)
        if (isQueryMode) {
            if (isGroupView) {
                // Group View: Must be linked to a group member OR be a general task (id=0)
                if (task.influencerId !== 0 && !queryInfluencerIds.has(task.influencerId)) {
                    return false;
                }
            } else { // Single Influencer View
                // Must match active influencer ID exactly
                if (task.influencerId !== activeinfluencerId) {
                    return false;
                }
            }
        }
        // No mode filter needed if !isQueryMode (Show All)

        // 3. Status Filter (Apply only in "All" or "Group" view, and if filter is not "ALL")
        // *** USES task.status DIRECTLY ***
        if ((!isQueryMode || isGroupView) && statusFilter !== "ALL") {
            // Filter based on the status stored *within the task object*
            if (task.status !== statusFilter) {
                return false;
            }
             // if (task.influencerId === 0) return false; // Uncomment if general tasks bypass status filter
        }
        // No status filter applied in Single Influencer view

        // If all checks passed
        return true;
    });

    // Optional: Sort overdue tasks by date ascending
    if (completionFilter === 'overdue') {
      partiallyFiltered.sort((a, b) => {
          // FIX: Provide a fallback for the date to prevent errors
          const dateComparison = (a.date || '').localeCompare(b.date || '');
          if (dateComparison !== 0) return dateComparison;
          
          // This line was already perfect! It prevents NaN if an ID is missing.
          return (a.id || 0) - (b.id || 0);
      });
    }

    return partiallyFiltered;

  }, [
      tasks, completionFilter, selectedDate, isQueryMode, isGroupView,
      activeinfluencerId, statusFilter, queryInfluencerIds
    ]);

  // --- Go To Email Handler (No changes needed here) ---
  const handleGoToEmailPage = useCallback(async (task) => {
       // (Keep existing implementation)
        if (!task.influencerId || task.influencerId === 0) { // Also check for 0
            console.warn("GoToEmail: Task is not linked to a specific influencer.", task);
            setLocalError("This task is not linked to a specific influencer.");
            return;
        }
        setLocalError(null);
        try {
            console.debug("[GoToEmail] Setting active context for:", task.influencerId);
            setActiveinfluencerId(task.influencerId);
            setIsGroupView(false); // Ensure single view mode
            setQueryMode(true);    // Ensure query mode

            console.log(`[GoToEmail] Navigating to /email for task ${task.id}, influencer ${task.influencerId}`);
            navigate("/email", {
                 replace: false,
                 state: { // Pass necessary info
                     taskId: task.id,
                     influencerId: task.influencerId,
                     taskNameMenu: task.nameMenu,
                     taskPrompt: task.prompt,
                     taskType: task.type,
                 }
            });
        } catch (error) {
            console.error("Error preparing for email page navigation:", error);
            setLocalError(error.message || "Failed to initiate navigation to email page.");
        }
    }, [setActiveinfluencerId, setQueryMode, setIsGroupView, navigate]); // Added setIsGroupView


  // --- Task Modification Handlers (Toggle/Delete - No changes needed) ---
  const handleToggleTaskCompletion = useCallback(async (taskId, currentCompletedStatus) => {
      if (isSaving) return;
      setIsSaving(true);
      setLocalError(null);
      try {
          // Call API first
          const result = await updateTask(taskId, { completed: !currentCompletedStatus });
          if(result && !result.error) {
              // Update state only on successful API call
              toggleTaskCompletion(taskId);
          } else {
               throw new Error(result?.message || "Failed to update task status in DB.");
          }
      } catch (error) {
          console.error("Error toggling task:", error);
          setLocalError(error.message || "Failed to update task status.");
      } finally {
          setIsSaving(false);
      }
  }, [updateTask, toggleTaskCompletion, isSaving]); // Added isSaving

  const handleDeleteTask = useCallback(async (taskId) => {
       if (isSaving) return;
      setIsSaving(true);
      setLocalError(null);
      try {
           // Call API first
          const result = await deleteTask(taskId);
           if(result && !result.error) {
               // Update state only on successful API call
               deleteTaskById(taskId);
           } else {
               throw new Error(result?.message || "Failed to delete task in DB.");
           }
      } catch (error) {
          console.error("Error deleting task:", error);
          setLocalError(error.message || "Failed to delete task.");
      } finally {
          setIsSaving(false);
      }
  }, [deleteTask, deleteTaskById, isSaving]); // Added isSaving

  // --- NEW: Reschedule Handlers ---
    const handleToggleRescheduleMode = useCallback(() => {
      setRescheduleMode(prev => !prev);
      setSelectedForReschedule(new Set()); // Clear selection on toggle
      setLocalError(null);
    }, []);
  
    const handleSelectTaskForReschedule = useCallback((taskId) => {
      setSelectedForReschedule(prevSet => {
          const newSet = new Set(prevSet);
          if (newSet.has(taskId)) {
              newSet.delete(taskId);
          } else {
              newSet.add(taskId);
          }
          return newSet;
      });
    }, []);
  
    const handleSelectAllForReschedule = useCallback((e) => {
      const isChecked = e.target.checked;
      if (isChecked) {
          // FIX: Add defensive checks for the filteredTasks array.
          const allVisibleIds = (filteredTasks || [])
              .filter(task => task && typeof task === 'object' && task.id !== undefined)
              .map(task => task.id);
          setSelectedForReschedule(new Set(allVisibleIds));
      } else {
          setSelectedForReschedule(new Set());
      }
    }, [filteredTasks]);

    const handleConfirmReschedule = useCallback(async () => {
      const idsToReschedule = Array.from(selectedForReschedule);
      if (idsToReschedule.length === 0) {
          setLocalError("No tasks selected to reschedule.");
          return;
      }
      
      const today = new Date();
      today.setHours(0,0,0,0);
      const targetDate = new Date(rescheduleDate + 'T00:00:00');
  
      if (targetDate < today) {
          setLocalError("Cannot reschedule tasks to a past date.");
          return;
      }
  
      setIsSaving(true);
      setLocalError(null);
  
      try {
          const result = await rescheduleTasks(idsToReschedule, rescheduleDate);
          if (result && !result.error) {
              console.log(`Successfully rescheduled ${result.updatedCount} tasks.`);
              handleToggleRescheduleMode(); // Exit reschedule mode on success
          } else {
              throw new Error(result?.message || "API error during reschedule.");
          }
      } catch (error) {
          setLocalError(error.message || "Failed to reschedule tasks.");
      } finally {
          setIsSaving(false);
      }
    }, [selectedForReschedule, rescheduleDate, rescheduleTasks, handleToggleRescheduleMode]);
  

  // --- Display Formatting ---

  const showRescheduleButton = useMemo(() => {
    // If we are currently in reschedule mode, the "Cancel" button must always be visible.
    if (rescheduleMode) {
        return true;
    }
    // Otherwise, show the "Reschedule" button only if there's at least one task
    // in the current view that is not completed.
    return filteredTasks.some(task => !task.completed);
}, [filteredTasks, rescheduleMode]);

  const displaySelectedDate = useMemo(() => {
    // --- ADD CHECK HERE ---
    if (selectedDate instanceof Date && !isNaN(selectedDate)) {
        // If selectedDate is a valid Date object
        return selectedDate.toLocaleDateString(undefined, {
            year: 'numeric', month: 'long', day: 'numeric'
        });
    } else {
        // Fallback if selectedDate is undefined, null, or invalid
        console.warn("ToDoPage: selectedDate is not a valid Date object yet.", selectedDate);
        // Return a default string or today's date formatted
        const today = new Date();
         return today.toLocaleDateString(undefined, {
             year: 'numeric', month: 'long', day: 'numeric'
         });
        // Or return an empty string or placeholder:
        // return "Loading date...";
    }
}, [selectedDate]); // Dependency remains selectedDate


  // --- Calendar Visuals ---
  const todayStr = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  // Enhanced tileClassName function
  const getTileClassName = useCallback(({ date, view }) => {
      if (view !== 'month') return ''; // Only style month view days

      const dateStr = formatDateToYYYYMMDD(date);
      const classes = [styles.calendarTile]; // Base class for all tiles

      // Check conditions and add classes
      if (dateStr === todayStr) classes.push(styles.todayTile);
      if (dateStr === formatDateToYYYYMMDD(selectedDate)) classes.push(styles.selectedTile);
      if (date.getDay() === 0 || date.getDay() === 6) classes.push(styles.weekendTile);
      if (hasTasksOnDate(date)) classes.push(styles.hasTasks); // Keep using the dot indicator

      return classes.join(' ');
  }, [selectedDate, todayStr, hasTasksOnDate]); // Dependencies


  // Calendar Legend Component
  const CalendarLegend = () => (
      <div className={styles.calendarLegend}>
          <div className={styles.legendItem}>
              <span className={`${styles.legendColorBox} ${styles.todayTile}`}></span> = Today
          </div>
          <div className={styles.legendItem}>
              <span className={`${styles.legendColorBox} ${styles.selectedTile}`}></span> = Selected
          </div>
          <div className={styles.legendItem}>
              <span className={`${styles.legendColorBox} ${styles.weekendTile}`}></span> = Weekend
          </div>
           <div className={styles.legendItem}>
               {/* Represent the 'hasTasks' dot */}
              <span className={`${styles.legendColorBox} ${styles.hasTasksLegendDot}`}></span> = Has Tasks
          </div>
      </div>
  );

  // --- Conditional Rendering Flags ---
  const showInfluencerSpecificHeader = isQueryMode && !isGroupView && activeInfluencer;
  const showGroupControls = isQueryMode && isGroupView;
  const canShowGroupButton = queryModeResults.length > 0; // Condition to show "Show Group Tasks"
  const isAddButtonDisabled = newTask.trim() === "" || isSaving || rescheduleMode;

  // --- JSX ---
  return (
    <div className={styles.todoPage}>
      <div className={styles.contentContainer}>
        {/* --- HEADER SECTION --- */}
        <div className={styles.headerContainer}>
          {/* Influencer Specific Details */}
          {showInfluencerSpecificHeader && activeInfluencer && (
            <div className={styles.headerInfo}>
              {activeInfluencer.thumbnail && (
                <img src={activeInfluencer.thumbnail} alt={`${activeInfluencer.name} Thumbnail`} className={styles.channelThumbnail} />
              )}
              <div className={styles.channelDetails}>
                 <h2 className={styles.channelName}>{activeInfluencer.name}</h2>
                 <p className={styles.channelStatus}>Status: <span>{activeInfluencer.status}</span></p>
              </div>
            </div>
          )}
          {/* Placeholder */}
          {!showInfluencerSpecificHeader && <div style={{ minWidth: '60px', minHeight: '60px' }}></div>}

          {/* Mode Control Buttons / Dropdown */}
          <div className={styles.modeControls}>
            {/* Show All Tasks Button */}
            {isQueryMode && (
              <button
                onClick={handleShowAllTasks}
                className={styles.modeButton}
                title="Show the to-do's of all influencers who have at least one pending to-do on any given day."
                disabled={isSaving}
              >
                  Show All Influencers
              </button>
            )}
            {/* Show Group Tasks Button */}
            {canShowGroupButton && ( // Only show if a group exists
              <button
                onClick={handleShowGroupTasks}
                className={styles.modeButton}
                title="Show the to-do's of all the influencers selected in the Query menu on any given day."
                // Disable if already in group view OR saving
                disabled={(isQueryMode && isGroupView) || isSaving}
              >
                Selected Influencers
              </button>
            )}
            {/* Influencer Dropdown (in Group View) */}
            {showGroupControls && (
              <select
                className={styles.influencerDropdown}
                onChange={handleSelectInfluencerFromDropdown}
                value={""} // Always show placeholder
                title="Show all the to-do's of a specific influencer on any given day."
                aria-label="Select Influencer"
                disabled={isSaving}
              >
                <option value="" disabled>-- Select Influencer --</option>
                {(queryModeResults || [])
                  // FIX: Filter out any null, undefined, or non-object items to prevent crashes.
                  .filter(item => item && typeof item === 'object' && item.influencerId !== undefined)
                  .map((item) => (
                      <option key={item.influencerId} value={item.influencerId}>
                          {/* UX IMPROVEMENT: Add a fallback for a missing name. */}
                          {item.name || 'Unnamed Option'}
                      </option>
                  ))}
              </select>
            )}

            {/* --- Status Filter Dropdown --- */}
            {/* Show only in All or Group View */}
            {(!isQueryMode || isGroupView) && ( // Simplified condition: show if NOT single influencer view
              <select
                className={styles.statusFilterDropdown} // Add specific style if needed
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                title="Filter the list of influencers's to-dos by status."
                aria-label="Filter by Influencer Status"
                disabled={isSaving}
              >
                {(MENU_STATUS || []).map((status) => (
                  <option key={status} value={status}>
                    {/* Add prefix for clarity */}
                    Status: {status}
                  </option>
                ))}
              </select>
            )}
            {/* --- End Status Filter --- */}

          </div>

          {/* Prev/Next Buttons */}
          {showInfluencerSpecificHeader && queryModeResults.length > 1 && ( // Only show if more than 1 in group
            <div className={styles.navigationButtons}>
              <button onClick={handlePrevious} disabled={queryModeResults.findIndex(item => item.influencerId === activeinfluencerId) <= 0 || isSaving}>Previous</button>
              <span>{queryModeResults.findIndex(item => item.influencerId === activeinfluencerId) + 1}/{queryModeResults.length}</span>
              <button onClick={handleNext} disabled={queryModeResults.findIndex(item => item.influencerId === activeinfluencerId) >= queryModeResults.length - 1 || isSaving}>Next</button>
            </div>
          )}
          {/* Placeholder */}
          {(!showInfluencerSpecificHeader || queryModeResults.length <= 1) && <div style={{ minWidth: '150px' }}></div>} {/* Adjust placeholder condition */}

        </div> {/* End Header Container */}

        {/* Global Loading Spinner */}
        {loading && <Spinner message="Loading tasks..." />}

        {/* --- MAIN CONTENT --- */}
        <div className={styles.mainContent}>
          {/* Calendar Section */}
          <div className={styles.calendarSection}>
            {/* Show spinner during CUD actions */}
            {isSaving && <Spinner size="small"/>}
            <Calendar
              onChange={onDateChange}
              value={selectedDate}
              className={styles.reactCalendar} // Base calendar style
              tileClassName={getTileClassName} // Use the new function
              minDate={new Date(new Date().getFullYear() - 1, 0, 1)} // Example: Limit past dates to 1 year
              maxDate={new Date(new Date().getFullYear() + 2, 11, 31)} // Example: Limit future dates
            />
            {/* Add the Legend */}
            <CalendarLegend />
          </div>

          {/* Tasks Section */}
          <div className={styles.tasksSection}>
            {/* Show spinner during CUD actions */}
            {isSaving && <Spinner size="small"/>}

            {/* --- Input Section --- */}
            <div className={styles.inputSection}>
              <input
                type="text"
                placeholder={isGroupView ? "Enter group task text..." : "Add a new task..."}
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                onKeyPress={handleKeyPress}
                title={isGroupView ?"To add a single to-do to multiple influencers first set in the calendar a due date you want the to-dos to be placed. This day is highlighted in blue. Then tick the check-box below if you want to distribute the group of influencers evenly among the interval of days between today and the due date. Then write the task in the input box. Finally click on 'Add Group Task'." : 
                                    "To add a single to-do to the selected influencer first set in the calendar a due date you want the to-do to be placed. This day is highlighted in blue. Then write the task in the input box. Finally click on 'Add Task'."}
                aria-label="New Task Input"
                disabled={isSaving} // Disable input while saving
              />
              {/* Add Button - behavior changes based on view */}
                <button
                  onClick={handleAddTask}
                  disabled={isAddButtonDisabled}
                  className={styles.addButton}
                  aria-label={isGroupView ? "Add Group Task" : "Add Task"}
                >
                  {isGroupView ? "Add Group Task" : "Add"}
                </button>
            </div>

            {/* --- Group Task Distribution Option --- */}
            {isGroupView && (
              <div className={styles.distributionOption}>
                  <label>
                      <input
                          type="checkbox"
                          checked={distributeTasks}
                          onChange={(e) => setDistributeTasks(e.target.checked)}
                          disabled={isSaving}
                      />
                      Distribute tasks evenly until due date (skips weekends)
                  </label>
              </div>
            )}


            {/* Local Error Display */}
            {localError && <p className={styles.errorMessage}>{localError}</p>}

            {/* Tasks Container */} 
            <div className={styles.tasksContainer}>

              {/* --- Completion/Overdue Filter --- */}
              <div className={styles.controlsContainer}>
                  <div className={styles.filterControls}>
                      <label htmlFor="completionFilterSelect">Filter:</label>
                      <select
                          id="completionFilterSelect"
                          className={styles.completionFilterDropdown}
                          value={completionFilter}
                          title="Filter the todo's list by its completion status."
                          onChange={(e) => setCompletionFilter(e.target.value)}
                          disabled={isSaving || loading || rescheduleMode}
                      >
                          <option value="all not overdue">All</option>
                          <option value="not completed">Not Completed</option>
                          <option value="completed">Completed</option>
                          <option value="overdue">Overdue</option>
                      </select>
                  </div>
                  {/* Use the new flag to conditionally render the button */}
                  {showRescheduleButton && (
                    <button 
                      onClick={handleToggleRescheduleMode} 
                      className={`${styles.toggleRescheduleButton} ${rescheduleMode ? styles.cancel : ''}`}
                      disabled={isSaving || loading}
                    >
                      <FaCalendarAlt style={{ marginRight: '8px' }}/>
                      {rescheduleMode ? "Cancel" : "Reschedule"}
                    </button>
                  )}
              </div>

              {/* --- NEW: Reschedule Actions UI --- */}
              {rescheduleMode && (
                  <div className={styles.rescheduleActions}>
                      {(isSaving && rescheduleMode) && <Spinner size="small"/>}
                      <label htmlFor="rescheduleDate" style={{fontWeight: 'bold'}}>New Date:</label>
                      <input
                          type="date"
                          id="rescheduleDate"
                          value={rescheduleDate}
                          onChange={(e) => setRescheduleDate(e.target.value)}
                          min={formatDateToYYYYMMDD(new Date())} // Prevent past dates
                          className={styles.rescheduleDateInput}
                          disabled={isSaving}
                      />
                      <div className={styles.selectAllContainer}>
                        <input
                            type="checkbox"
                            id="selectAll"
                            onChange={handleSelectAllForReschedule}
                            checked={filteredTasks.length > 0 && selectedForReschedule.size === filteredTasks.length}
                            disabled={isSaving || filteredTasks.length === 0}
                        />
                        <label htmlFor="selectAll">Select All</label>
                      </div>
                      <button 
                          className={styles.confirmButton}
                          onClick={handleConfirmReschedule}
                          disabled={isSaving || selectedForReschedule.size === 0}
                      >
                          Confirm ({selectedForReschedule.size})
                      </button>
                  </div>
              )}

              {/* --- End Completion/Overdue Filter --- */}

              <h3 className={styles.tasksHeader}>
                {/* Dynamically adjust header based on filter */}
                {completionFilter === 'overdue' ? 'Overdue Tasks' : `Tasks for ${displaySelectedDate}`}
                {/* Add context */}
                {isQueryMode && !isGroupView && activeInfluencer ? ` - ${activeInfluencer.name}` : ''}
                {isQueryMode && isGroupView ? ' (Selected Influencers)' : ''}
                {!isQueryMode ? ' (All Influencers)' : ''}
                {/* Add status filter info if applied */}
                {statusFilter !== 'ALL' && (!isQueryMode || isGroupView) ? ` [Status: ${statusFilter}]` : ''}
              </h3>

              {/* Task List or Messages */}
              {loading ? ( <p>Loading tasks...</p> ) : filteredTasks.length === 0 ? (
                <p className={styles.noTasks}>No tasks found matching the current filters.</p>
              ) : (
                <ul className={styles.tasksList}>
                  {(filteredTasks || [])
                    .filter(task => task && typeof task === 'object' && task.id !== undefined)
                    .map((task) => {
                      const displayedText = (isGroupView || !isQueryMode) && task.influencerId
                          ? `[${getChannelName(task.influencerId) || 'Unknown Channel'}]: ${task.text || 'No Task Text'}`
                          : task.text || 'No Task Text';

                      const prefix = completionFilter === 'overdue' && task.date
                          ? `(${task.date}) [Overdue] `
                          : "";

                      const isClickable = !rescheduleMode && task.influencerId && (!isQueryMode || isGroupView);

                      return (
                        <li 
                          key={task.id} 
                          className={`${styles.taskItem} ${completionFilter === 'overdue' ? styles.overdueTask : ''} ${isClickable ? styles.clickableTask : ''}`}
                          onClick={isClickable ? () => handleInfluencerSelect(task.influencerId) : undefined}
                        >
                        {rescheduleMode && (
                          <input
                            type="checkbox"
                            className={styles.taskRescheduleCheckbox}
                            checked={selectedForReschedule.has(task.id)}
                            onChange={() => handleSelectTaskForReschedule(task.id)}
                            onClick={(e) => e.stopPropagation()}
                            disabled={isSaving}
                          />
                        )}
                        <input
                          type="checkbox"
                          checked={task.completed}
                          onChange={() => handleToggleTaskCompletion(task.id, task.completed)}
                          className={styles.taskCheckbox}
                          disabled={isSaving || rescheduleMode}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span className={`${styles.taskText} ${task.completed ? styles.completed : ""}`}>
                          {prefix}{displayedText} {(task.type === 'AUTOGROUP') ? '(G)' : ''}
                        </span>
                          <div className={styles.taskActions}>
                            {validGoTypes.includes(task.type) && task.influencerId && !task.completed && (
                              <button
                                className={styles.goButton}
                                onClick={(e) => { e.stopPropagation(); handleGoToEmailPage(task); }}
                                disabled={isSaving || rescheduleMode}
                              >
                                Do-it!
                              </button>
                            )}
                            <button
                                className={styles.deleteButton}
                                onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                                disabled={isSaving || rescheduleMode}
                              >
                                <FaTrash />
                              </button>
                          </div>
                        </li>
                      );
                    })}
                </ul>
              )}
            </div> {/* End Tasks Container */}
          </div> {/* End Tasks Section */}
        </div> {/* End Main Content */}
      </div> {/* End content container*/}
      {/* --- ADD THIS MODAL COMPONENT HERE --- */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleConfirmGroupAddTask}
        title="Confirm Group Task Creation"
        confirmText="Create Tasks"
        isConfirming={isSaving}
      >
        {modalMessage}
      </ConfirmationModal>
    </div> // End Todo Page
  );
};

export default ToDoPage;

// --- END OF FILE ToDoPage.js ---

